'use client';

import { useRef, useImperativeHandle, forwardRef, useMemo, useEffect } from 'react';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { tv } from 'tailwind-variants';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { renderFormField } from 'helpers/FormFields';
import GuidanceSection from './GuidanceSection';
import { Field } from '@sitecore-content-sdk/nextjs';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { RemixIcon } from 'helpers/RemixIcon';

type SubmitHandler = (values: Record<string, string>) => Promise<void> | void;

export type ConveyancingStep3Props = {
  fields?: CustomForm.Form['fields'] & { submitStatus?: 'idle' | 'loading' | 'success' | 'error' };
  onSubmit?: SubmitHandler;
  guidanceBlock?: {
    title?: Field<string>;
    description?: Field<string>;
  };
  onValidationChange?: (hasErrors: boolean) => void;
  initialValues?: Record<string, string>;
};

export type ConveyancingStep3Ref = {
  submitForm: () => Promise<Record<string, string> | null>;
  getFormValues: () => Record<string, string>;
};

// Recursively walk through fields (supports "section" type nesting)
const processFieldsRecursively = (
  formFields: CustomForm.FormField_Item[] = [],
  processor: (field: CustomForm.FormField_Item) => void
) => {
  formFields.forEach((field) => {
    const fieldConfig = field.fields;
    const fieldType = fieldConfig?.fieldType?.value;
    const normalizedType = typeof fieldType === 'string' ? fieldType.toLowerCase() : '';
    if (normalizedType === 'section') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sectionFields = fieldConfig?.selectOptionsAndSection || [];
      processFieldsRecursively(sectionFields, processor);
    } else if (normalizedType !== 'recaptcha') {
      processor(field);
    }
  });
};

// Create initial values similar to DynamicForm
const generateInitialValues = (formFields: CustomForm.FormField_Item[] = []) => {
  const initialValues: Record<string, string> = {};

  processFieldsRecursively(formFields, (field) => {
    const fieldConfig = field.fields;
    const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;

    if (fieldName) {
      initialValues[fieldName] = fieldConfig?.defaultValue?.value || '';
    }
  });

  return initialValues;
};

// Build Yup schema similar to DynamicForm
const generateValidationSchema = (formFields: CustomForm.FormField_Item[] = []) => {
  const validationFields: Record<string, Yup.StringSchema> = {};

  processFieldsRecursively(formFields, (field) => {
    const fieldConfig = field.fields;
    const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
    const fieldType = fieldConfig?.fieldType?.value;

    if (fieldName) {
      let fieldValidation = Yup.string();

      if (fieldConfig?.required?.value) {
        fieldValidation = fieldValidation.required(
          fieldConfig?.requiredValidationMessage?.value ||
            `${fieldConfig?.label?.value} is required`
        );
      } else {
        fieldValidation = fieldValidation.optional();
      }

      if (fieldType === 'email') {
        fieldValidation = fieldValidation.email(
          fieldConfig?.validationErrorMessage?.value || 'Please enter a valid email address'
        );
      }

      if (
        fieldConfig?.validationPattern?.value &&
        fieldConfig.validationPattern.value.trim() !== ''
      ) {
        try {
          const regex = new RegExp(fieldConfig.validationPattern.value);
          fieldValidation = fieldValidation.matches(
            regex,
            fieldConfig?.validationErrorMessage?.value || 'Invalid format'
          );
        } catch (_error) {
          // Ignore invalid regex patterns
        }
      }

      const minLimit = fieldConfig?.minCharacterLimit?.value;
      const maxLimit = fieldConfig?.characterLimit?.value;

      if (minLimit && typeof minLimit === 'number' && minLimit > 0) {
        fieldValidation = fieldValidation.min(
          minLimit,
          fieldConfig?.minCharacterLimitValidationMessage?.value ||
            `Minimum ${minLimit} characters required`
        );
      }
      if (maxLimit && typeof maxLimit === 'number' && maxLimit > 0) {
        fieldValidation = fieldValidation.max(
          maxLimit,
          fieldConfig?.maxCharacterLimitValidationMessage?.value ||
            `Maximum ${maxLimit} characters allowed`
        );
      }

      validationFields[fieldName] = fieldValidation;
    }
  });

  return Yup.object().shape(validationFields);
};

// Get all required field names recursively
const getRequiredFieldNames = (formFields: CustomForm.FormField_Item[] = []): string[] => {
  const requiredFields: string[] = [];

  processFieldsRecursively(formFields, (field) => {
    const fieldConfig = field.fields;
    const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
    if (fieldName && fieldConfig?.required?.value) {
      requiredFields.push(fieldName);
    }
  });

  return requiredFields;
};

// Remove any reCAPTCHA fields from the tree (including inside sections)
const sanitizeFieldsForRendering = (
  formFields: CustomForm.FormField_Item[] = []
): CustomForm.FormField_Item[] => {
  const cleaned: CustomForm.FormField_Item[] = [];

  formFields.forEach((field) => {
    const fieldType = field.fields?.fieldType?.value;
    const normalizedType = typeof fieldType === 'string' ? fieldType.toLowerCase() : '';
    if (normalizedType === 'recaptcha') {
      return;
    }

    if (normalizedType === 'section') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sectionFields = (field.fields?.selectOptionsAndSection || []) as any;
      const filteredChildren = sanitizeFieldsForRendering(sectionFields);
      const cloned: CustomForm.FormField_Item = {
        ...field,
        fields: {
          ...field.fields,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          selectOptionsAndSection: filteredChildren as any,
        },
      };
      cleaned.push(cloned);
      return;
    }

    cleaned.push(field);
  });

  return cleaned;
};

const STYLES = tv({
  slots: {
    container: ['w-full', 'flex', 'flex-col', 'gap-general-spacing-gutter'],
    form: ['lg:w-[51.17%]', 'order-2', 'lg:order-1'],
    formGrid: ['flex', 'flex-col', 'lg:flex-row', 'lg:gap-margin-loose', 'gap-spacing-spacing-24'],
    fieldsColumn: ['flex', 'flex-col', 'gap-general-spacing-gutter'],
    fieldGroup: ['flex', 'flex-col', 'gap-general-spacing-gutter'],
    Message: [
      'flex',
      'items-start',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      'gap-space-between-less',
      'rounded-component-alert-bg-radius',
      'text-color-text-text-info',
    ],
    MessageText: [
      'text-component-alert-text-priority',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-normal',
    ],
  },
  variants: {
    submitStatus: {
      error: {
        Message: ['bg-component-alert-bg-priority', '[&_svg]:fill-color-fill-fill-critical'],
        MessageText: ['text-color-text-text-info'],
      },
      success: {
        Message: ['bg-component-alert-by-success', '[&_svg]:fill-color-fill-fill-success'],
        MessageText: ['text-color-text-text-success-hover'],
      },
    },
  },
  defaultVariants: {
    submitStatus: 'error',
  },
});

const { container, form, formGrid, fieldsColumn, fieldGroup, Message, MessageText } = STYLES();

const ConveyancingStep3 = forwardRef<ConveyancingStep3Ref, ConveyancingStep3Props>((props, ref) => {
  const { fields, onSubmit, guidanceBlock, onValidationChange, initialValues } = props;
  const formFields = fields?.formFields || [];
  const sanitizedFormFields = sanitizeFieldsForRendering(formFields);
  const prevHasErrorsRef = useRef<boolean | null>(null);
  const requiredFieldNames = getRequiredFieldNames(sanitizedFormFields);
  const formikRef = useRef<FormikProps<Record<string, string>>>(null);

  // Merge default initial values with provided initial values (from sessionStorage)
  const defaultInitialValues = generateInitialValues(sanitizedFormFields);
  const mergedInitialValues = useMemo(() => {
    return { ...defaultInitialValues, ...initialValues };
  }, [defaultInitialValues, initialValues]);

  // Update form values when initialValues change (e.g., when navigating back)
  const prevInitialValuesRef = useRef<Record<string, string> | undefined>(initialValues);
  useEffect(() => {
    if (
      formikRef.current &&
      initialValues &&
      JSON.stringify(prevInitialValuesRef.current) !== JSON.stringify(initialValues)
    ) {
      const updatedValues = { ...defaultInitialValues, ...initialValues };
      formikRef.current.setValues(updatedValues);
      prevInitialValuesRef.current = initialValues;
    }
  }, [initialValues, defaultInitialValues]);

  useImperativeHandle(ref, () => ({
    submitForm: async () => {
      if (!formikRef.current) return null;
      const errors = await formikRef.current.validateForm();
      if (Object.keys(errors).length > 0) {
        formikRef.current.setTouched(
          Object.keys(errors).reduce(
            (acc, key) => {
              acc[key] = true;
              return acc;
            },
            {} as Record<string, boolean>
          )
        );
        return null;
      }
      const values = formikRef.current.values;
      if (onSubmit) {
        await onSubmit(values);
      }
      return values;
    },
    getFormValues: () => {
      return formikRef.current?.values || {};
    },
  }));

  if (!fields || !sanitizedFormFields?.length) {
    return <div />;
  }

  return (
    <section className={container()}>
      <div className={formGrid()}>
        <Formik
          innerRef={formikRef}
          initialValues={mergedInitialValues}
          validationSchema={generateValidationSchema(sanitizedFormFields)}
          enableReinitialize={true}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (onSubmit) {
                await onSubmit(values);
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ errors, touched, values }) => {
            // Check if there are any errors in touched fields
            const hasErrors = Object.keys(errors).some((key) => errors[key] && touched[key]);
            // Check if all required fields are filled (have non-empty values)
            const allRequiredFieldsFilled = requiredFieldNames.every(
              (fieldName) => values[fieldName] && String(values[fieldName]).trim() !== ''
            );
            // Button should be disabled if there are errors OR if not all required fields are filled
            const shouldReportErrors = hasErrors || !allRequiredFieldsFilled;

            // Report validation state to parent when it changes
            // Defer callback to avoid updating parent state during render
            if (prevHasErrorsRef.current !== shouldReportErrors && onValidationChange) {
              prevHasErrorsRef.current = shouldReportErrors;
              setTimeout(() => {
                onValidationChange(shouldReportErrors);
              }, 0);
            }

            return (
              <Form className={form()}>
                <div className={fieldsColumn()}>
                  <div className={fieldGroup()}>
                    {sanitizedFormFields.map((field: CustomForm.FormField_Item) => {
                      if (field.fields?.fieldType?.value === 'section') {
                        return renderFormField(
                          field,
                          errors,
                          touched,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          1
                        );
                      }
                      return renderFormField(field, errors, touched);
                    })}
                  </div>
                  {fields?.submitStatus === 'error' && (
                    <div className={Message()}>
                      <RemixIcon name="alert-line" size="md" />
                      <RichTextWrapper
                        field={fields.failureMessage}
                        className={MessageText({ submitStatus: fields?.submitStatus })}
                      />
                    </div>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>
        {(guidanceBlock?.title?.value || guidanceBlock?.description?.value) && (
          <GuidanceSection guidanceBlock={guidanceBlock} />
        )}
      </div>
    </section>
  );
});

ConveyancingStep3.displayName = 'ConveyancingStep3';

export default ConveyancingStep3;
